import {
  format,
  intlFormat,
  isValid,
  parse,
  sub,
  differenceInDays,
  parseISO,
  addDays,
} from "date-fns";

// These are useful and this saves brand apps from having to install date-fns
export { isBefore, isAfter } from "date-fns";

const formatDateWithYear = (date: Date) =>
  isValid(date)
    ? intlFormat(
        date,
        {
          year: "numeric",
          month: "short",
          day: "numeric",
        },
        { locale: "en-US" },
      )
    : "";

const formatDateWithoutYear = (date: Date) =>
  isValid(date)
    ? intlFormat(
        date,
        {
          month: "short",
          day: "numeric",
        },
        { locale: "en-US" },
      )
    : "";

const formatDayAndTime = (date: Date) =>
  isValid(date)
    ? intlFormat(
        date,
        {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          timeZoneName: "short",
        },
        { locale: "en-US" },
      )
    : "";

const formatTimeWithZone = (date: Date) =>
  isValid(date)
    ? intlFormat(
        date,
        {
          hour: "numeric",
          minute: "numeric",
          timeZoneName: "short",
        },
        { locale: "en-US" },
      )
    : "";

const formatRelativeDate = (value: number, unit: Intl.RelativeTimeFormatUnit) =>
  new Intl.RelativeTimeFormat("en-US", {
    numeric: "auto",
  }).format(value, unit);

const msSecond = 1000;
const msMinute = msSecond * 60;
const ms1Hour = msMinute * 60;
const ms1Day = ms1Hour * 24;
const ms1Week = ms1Day * 7;

export const isToday = (date: Date) => {
  const dateNow = new Date();

  return (
    dateNow.getFullYear() === date.getFullYear() &&
    dateNow.getMonth() === date.getMonth() &&
    dateNow.getDate() === date.getDate()
  );
};

export const formatHubDate = (isoString: string): string => {
  if (!isoString.length) {
    return "";
  }
  const dateNow = new Date();
  const date = new Date(isoString);
  const msDiff = dateNow.getTime() - date.getTime();

  const isCurrentDay = isToday(date);

  if (!(date instanceof Date) || isNaN(date.valueOf())) {
    return "";
  } else if (msDiff < msSecond) {
    return "Now";
  } else if (msDiff < msMinute) {
    // < 1 minute -> 17 seconds ago
    return formatRelativeDate(Math.floor((-1 * msDiff) / msSecond), "seconds");
  } else if (msDiff < msMinute * 2) {
    // < 2 minutes -> A minute ago
    return "A minute ago";
  } else if (msDiff < ms1Hour) {
    // < 1 hours -> 17 minutes ago
    return formatRelativeDate(Math.floor((-1 * msDiff) / msMinute), "minutes");
  } else if (msDiff < ms1Hour * 1.5) {
    // < 1.5 hours -> An hour ago
    return "An hour ago";
  } else if (msDiff < ms1Hour * 3) {
    // < 3 hours -> Two hours ago
    return "Two hours ago";
  } else if (isCurrentDay) {
    // > Midnight today and < 3 hours ago -> Today at HH:MM AM/PM EST
    return formatTimeWithZone(date);
  } else if (dateNow.getFullYear() - date.getFullYear() > 0) {
    // Previous calendar year -> date with year
    return formatDateWithYear(date);
  } else {
    // Current calendar year -> date without year
    return formatDateWithoutYear(date);
  }
};

export const formatCycloneDate = (isoString: string): string => {
  if (!isoString.length) {
    return "";
  }
  const dateNow = new Date();
  const date = new Date(isoString);
  const msDiff = dateNow.getTime() - date.getTime();

  if (!(date instanceof Date) || isNaN(date.valueOf())) {
    return "";
  } else if (msDiff < msSecond) {
    return "Now";
  } else if (msDiff < msMinute) {
    // 1s - 59s
    return `${Math.floor(msDiff / msSecond)}s`;
  } else if (msDiff < ms1Hour) {
    // 1m - 59m
    return `${Math.floor(msDiff / msMinute)}m`;
  } else if (msDiff < ms1Day) {
    // 1h - 23h
    return `${Math.floor(msDiff / ms1Hour)}h`;
  } else if (msDiff < ms1Week) {
    // 1d - 6d
    return `${Math.floor(msDiff / ms1Day)}d`;
  } else if (dateNow.getFullYear() - date.getFullYear() > 0) {
    // Previous calendar year -> date with year
    return format(date, "MM/dd/yyyy");
  } else {
    // Current calendar year -> date without year
    return formatDateWithoutYear(date);
  }
};

export const formatNotificationsDate = (isoString: string): string => {
  if (!isoString.length) {
    return "";
  }
  const dateNow = new Date();
  const date = new Date(isoString);
  const msDiff = dateNow.getTime() - date.getTime();

  if (!(date instanceof Date) || isNaN(date.valueOf())) {
    return "";
  } else if (msDiff < msSecond) {
    return "Now";
  } else if (msDiff < msMinute) {
    // 1s - 59 seconds ago
    const seconds = Math.floor(msDiff / msSecond);
    return `${seconds} ${seconds === 1 ? "second" : "seconds"} ago`;
  } else if (msDiff < ms1Hour) {
    // 1m - 59 minutes ago
    const minutes = Math.floor(msDiff / msMinute);
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  } else if (msDiff < ms1Day) {
    // 1h - 23 hours ago
    const hours = Math.floor(msDiff / ms1Hour);
    return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  } else if (msDiff < ms1Week) {
    // 1d - 6 days ago
    const days = Math.floor(msDiff / ms1Day);
    return `${days} ${days === 1 ? "day" : "days"} ago`;
  } else if (dateNow.getFullYear() - date.getFullYear() > 0) {
    // Previous calendar year -> date with year
    return format(date, "MM/dd/yyyy");
  } else {
    // Current calendar year -> date without year
    return formatDateWithoutYear(date);
  }
};

export const formatStreamDate = (isoString: string): string => {
  if (!isoString.length) {
    return "";
  }
  const date = new Date(isoString);

  if (!(date instanceof Date) || isNaN(date.valueOf())) {
    return "";
  } else {
    return formatDayAndTime(date);
  }
};

export const formatCoralAbsoluteDate = (isoString: string): string =>
  intlFormat(
    new Date(isoString),
    {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    },
    { locale: "en-US" },
  );

export const formatMmaEventDate = (isoString: string): string =>
  intlFormat(
    new Date(isoString),
    {
      month: "long",
      day: "numeric",
      year: "numeric",
    },
    { locale: "en-US" },
  );

export const formatBylineDate = (isoString: string): string =>
  intlFormat(
    new Date(isoString),
    {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      timeZoneName: "short",
    },
    { locale: "en-US" },
  );

export const formatBreakerDate = (isoString: string): string => {
  return formatDateWithoutYear(new Date(isoString));
};

export const formatUserJoinDate = (isoString: string): string => {
  return formatDateWithYear(new Date(isoString));
};

export const getDateAndTime = (
  isoString: string,
): { date: string; time: string } => {
  if (!isoString.length) {
    return { date: "", time: "" };
  }
  const date = new Date(isoString);

  if (!(date instanceof Date) || isNaN(date.valueOf())) {
    return { date: "", time: "" };
  } else {
    return {
      date: formatDateWithYear(date),
      time: formatTimeWithZone(date),
    };
  }
};

export const formatQuickPostPermalinkDate = (isoString: string): string => {
  const { date, time } = getDateAndTime(isoString);

  return `${date} at ${time}`;
};

export const getLocaleTimeString = (locale: string): string =>
  new Date().toLocaleTimeString("en-US", {
    timeZone: locale,
  });

export const getMostRecentDate = (
  firstIsoString: string | undefined,
  secondIsoString: string | undefined,
) => {
  if (!firstIsoString && !secondIsoString) {
    return null;
  } else if (firstIsoString && !secondIsoString) {
    return firstIsoString;
  } else if (!firstIsoString && secondIsoString) {
    return secondIsoString;
  } else if (
    firstIsoString &&
    secondIsoString &&
    new Date(firstIsoString) > new Date(secondIsoString)
  ) {
    return firstIsoString;
  } else {
    return secondIsoString;
  }
};

export const getDaysAgo = (daysAgo: number) =>
  sub(new Date(), { days: daysAgo });

export const subtractDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
};

// Returns current data in the format "MM-DD-YY"
export const getCurrentDateAsString = () => {
  const currentDate = new Date();
  const day = currentDate.getDate().toString().padStart(2, "0");
  const month = (currentDate.getMonth() + 1).toString().padStart(2, "0"); // Month is zero-based, so we add 1
  const year = currentDate.getFullYear().toString().slice(-2); // Extract the last two digits of the year

  return `${month}-${day}-${year}`;
};

export function insertDateIntoFilename(
  filename: string,
  extension: string = "csv",
): string {
  // Get the current date as a formatted string
  const currentDate = getCurrentDateAsString();

  // Split the filename into its base name and extension parts
  const parts = filename.split(".");
  // Remove the last part (extension) if it exists
  const filenameExtension = parts.length > 1 ? parts.pop() : undefined;
  // Join the remaining parts to get the base name
  const baseName = parts.join(".");

  // Insert the current date before the extension if the base name does not contain a date
  const newFilename = `${baseName}${/\b\d{2}-\d{2}-\d{2}\b/.test(baseName) ? "" : `-${currentDate}`}.${
    filenameExtension || extension
  }`;

  return newFilename;
}

export function removeDateFromFilename(filename: string): string {
  // Split the filename into its base name and extension parts
  const parts = filename.split(".");
  const extension = parts.pop(); // Remove the last part (extension)

  // Join the remaining parts to get the base name
  let baseName = parts.join(".");

  // Check if the base name contains a date at the end
  const dateRegex = /\d{2}-\d{2}-\d{2}$/;
  const match = baseName.match(dateRegex);
  if (match) {
    // If a date is found, remove it from the base name
    baseName = baseName.slice(0, -9).trim(); // Trim any whitespace
  }

  // Reconstruct the filename without the date
  const newFilename = extension ? `${baseName}.${extension}` : baseName;
  return newFilename;
}

export const getCurrentDateYYYYM = (): string => {
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1; // Month is zero-based, so we add 1

  return `${year}/${month}`;
};

/// Check if a date is older than a certain number of days
export const isDateOlderThanFn = (date: string, daysAgo: number): boolean => {
  return differenceInDays(new Date(), parseISO(date)) >= daysAgo;
};

const parseIn5Date = (dateString: string): Date | null => {
  const mmddyyyy = parse(dateString, "MM-dd-yyyy", new Date());
  if (!isNaN(mmddyyyy.getTime())) {
    return mmddyyyy;
  }

  const yyyymmdd = parse(dateString, "yyyy-MM-dd", new Date());
  if (!isNaN(yyyymmdd.getTime())) {
    return yyyymmdd;
  }

  return null;
};

export const formatIn5GameDate = (dateString: string) => {
  try {
    const parsedDate = parseIn5Date(dateString);
    if (!parsedDate) {
      throw new Error("Invalid date format");
    }

    return format(parsedDate, "MMMM d, yyyy");
  } catch (error) {
    console.error("Error parsing date:", dateString, error);
    return dateString; // Return original string if parsing fails
  }
};

/**
 * Formats an In-5 date string for compact promo units.
 * Accepts MM-DD-YYYY and YYYY-MM-DD inputs.
 */
export const formatIn5PromoDate = (dateString: string) => {
  try {
    const parsedDate = parseIn5Date(dateString);
    if (!parsedDate) {
      throw new Error("Invalid date format");
    }

    return format(parsedDate, "MMM dd yyyy");
  } catch (error) {
    console.error("Error parsing In-5 promo date:", dateString, error);
    return dateString;
  }
};

/**
 * Converts a Date or date string (in MM-DD-YYYY or YYYY-MM-DD) to MM-DD-YYYY format.
 * Returns the original string if parsing fails.
 */
export const toMMDDYYYY = (input: Date | string): string => {
  let date: Date;
  if (input instanceof Date) {
    date = input;
  } else if (typeof input === "string") {
    // Try MM-DD-YYYY
    date = parse(input, "MM-dd-yyyy", new Date());
    if (isNaN(date.getTime())) {
      // Try YYYY-MM-DD
      date = parse(input, "yyyy-MM-dd", new Date());
    }
    if (isNaN(date.getTime())) {
      return input; // fallback to original string
    }
  } else {
    return "";
  }
  return format(date, "MM-dd-yyyy");
};

/**
 * Gets the current game date based on the 5am Eastern reset time.
 * Before 5am Eastern: returns yesterday's date
 * After 5am Eastern: returns today's date
 *
 * @returns The current game date in MM-DD-YYYY format
 */
export const getCurrentGameDate = (): string => {
  const nowEastern = new Date(
    new Date().toLocaleString("en-US", { timeZone: "America/New_York" }),
  );

  // Get today's date at 5am Eastern time
  const now = new Date();
  const resetTime = new Date(
    now.toLocaleString("en-US", { timeZone: "America/New_York" }),
  );
  resetTime.setHours(5, 0, 0, 0);

  return nowEastern < resetTime
    ? toMMDDYYYY(new Date(resetTime.getTime() - 24 * 60 * 60 * 1000)) // yesterday
    : toMMDDYYYY(resetTime); // today
};

export const addDaysToDate = (date: Date, days: number): Date => {
  return addDays(date, days);
};

/**
 * Adds minutes to a time string, respecting 24-hour format boundaries.
 * @param timeString - Time in HH:MM format
 * @param minutes - Minutes to add
 * @returns Time string in HH:MM format, clamped between 00:00 and 23:59
 */
export const addMinutesToTime = (
  timeString: string,
  minutes: number,
): string => {
  const [hours, mins] = timeString.split(":").map(Number);
  const totalMinutes = (hours || 0) * 60 + (mins || 0) + minutes;
  const clampedMinutes = Math.max(0, Math.min(1439, totalMinutes)); // 23:59 = 1439 minutes
  const newHours = Math.floor(clampedMinutes / 60);
  const newMins = clampedMinutes % 60;
  return `${newHours.toString().padStart(2, "0")}:${newMins.toString().padStart(2, "0")}`;
};

/**
 * Subtracts minutes from a time string, respecting 24-hour format boundaries.
 * @param timeString - Time in HH:MM format
 * @param minutes - Minutes to subtract
 * @returns Time string in HH:MM format, clamped between 00:00 and 23:59
 */
export const subtractMinutesFromTime = (
  timeString: string,
  minutes: number,
): string => {
  return addMinutesToTime(timeString, -minutes);
};

/**
 * Gets the reservation date in YYYY-MM-DD format.
 * @param date - The date to format.
 * @returns The reservation date in YYYY-MM-DD format.
 */
export const getReservationDate = (
  date: Date | string = new Date(),
): string => {
  return format(new Date(date), "yyyy-MM-dd");
};

/**
 * Converts 24-hour format to 12-hour AM/PM format
 * @param hour - Hour in 24-hour format (0-23)
 * @param minute - Minute (0-59)
 * @returns Time string in 12-hour format (e.g., "12:00 AM", "1:30 PM")
 */
export const formatTo12Hour = (hour: number, minute: number): string => {
  return format(new Date(0, 0, 0, hour, minute), "h:mm a");
};

/**
 * Formats a time string in "HH:MM" format to 12-hour format (e.g., "10:00 pm").
 * @param timeString - Time string in "HH:MM" format
 * @returns Formatted time string in 12-hour format (lowercase)
 */
export const formatTimeString = (timeString: string): string => {
  const [hoursStr, minutesStr] = String(timeString).split(":");
  const hours = Number(hoursStr) || 0;
  const minutes = Number(minutesStr) || 0;
  return formatTo12Hour(hours, minutes).toLowerCase();
};

/**
 * Converts a time string in "HH:MM" format to total minutes since midnight.
 * @param timeString - Time string in "HH:MM" format
 * @returns Total minutes since midnight (0-1439)
 */
export const timeStringToMinutes = (timeString: string): number => {
  const [hoursStr, minutesStr] = String(timeString).split(":");
  const hours = Number(hoursStr) || 0;
  const minutes = Number(minutesStr) || 0;
  return hours * 60 + minutes;
};

/**
 * Compares two time strings in "HH:MM" format.
 * @param time1 - First time string
 * @param time2 - Second time string
 * @returns Negative if time1 < time2, positive if time1 > time2, 0 if equal
 */
export const compareTimeStrings = (time1: string, time2: string): number => {
  return timeStringToMinutes(time1) - timeStringToMinutes(time2);
};

/**
 * Returns the time until the expiration date in milliseconds.
 * @param expirationDateString - The expiration date string in the format "YYYY-MM-DDTHH:MM:SSZ".
 * @returns The time until the expiration date in milliseconds.
 */
export const getTimeUntilExpirationInMS = (expirationDateString: string) => {
  const expirationDate = new Date(expirationDateString);
  const now = new Date();
  return Math.floor(expirationDate.getTime() - now.getTime());
};

/**
 * Converts UTC hour to local time string.
 * For example, if hour is 5pm UTC, it returns the local time equivalent of 12PM EST, 10AM PST
 * @param hour hour
 * @returns local time string in "${HOUR}AM/PM TZ" format
 */
export const getLocalTimeFromUTC = (hour: number) => {
  const date = new Date();
  date.setUTCHours(hour, 0, 0, 0);

  const formattedDate = intlFormat(
    date,
    {
      hour: "numeric",
      hour12: true,
      timeZoneName: "short",
    },
    { locale: "en-US" },
  );

  // Converts "12 PM EST" to "12PM EST"
  return formattedDate.replace(" AM", "AM").replace(" PM", "PM");
};
